/**
 * The IframedContent component is used to render 3rd party pages withing the context of the ulta domain so that we are able to contain that code without the possibillity of it&#x27;s contents cuasing impact to hte ulta site.
 * @module views/components/IframedContent
 * @memberof -Common
 */
import './IframedContent.scss';

import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import IframeResizer from 'iframe-resizer-react';
import PropTypes from 'prop-types';

import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';

import * as utils from './IframedContent';
/**
  * Represents a IframedContent component
  *
  * @method
  * @param {IframedContentProps} props - React properties passed from composition
  * @returns IframedContent
  */
export const IframedContent = function( props ){
  const { isLocal } = useAppConfigContext();
  const [iframeId] = useState( `IframedContent__${props.id}` );
  const [iframeSrc, setIframeSrc] = useState();
  const {
    contentUrl,
    fullScreenPageDisplay = defaultProps.fullScreenPageDisplay
  } = props;

  let iframeRef = useRef();

  const [iframeContentLoaded, setIframeContentLoaded] = useState( false );
  const [iframeNoScriptMarkup, setIframeNoScriptMarkup] = useState( );

  useEffect( ()=> {
    utils.handleIframeContent( { iframeSrc, iframeRef, iframeContentLoaded }, { setIframeNoScriptMarkup } );

  }, [iframeRef.current, iframeContentLoaded] );

  useEffect( ()=> {
    utils.isUltaDomain( { contentUrl, isLocal }, { setIframeSrc } );
  }, [] );

  return (
    <div
      ref={ iframeRef }
      className={
        classNames( 'IframedContent', {
          'IframedContent--fullScreenDisplay': fullScreenPageDisplay
        } )
      }
    >
      { iframeSrc &&
      <>
        { fullScreenPageDisplay &&
        <IframeResizer
          log
          onLoad={ handleIframeLoaded( { setIframeContentLoaded } ) }
          heightCalculationMethod='max'
          src={ iframeSrc }
          autoResize={ false }
          frameBorder='0'
          title={ contentUrl }
          scrolling={ true }
        />
        }
        { !fullScreenPageDisplay &&
        <iframe
          onLoad={ handleIframeLoaded( { setIframeContentLoaded } ) }
          id={ iframeId }
          title={ contentUrl }
          src={ contentUrl }
          frameBorder='0'
        />
        }
        { iframeNoScriptMarkup &&
        <noscript>
          { iframeNoScriptMarkup }
        </noscript>
        }
      </>
      }
    </div>
  );
};

/**
  * A curreid event handler for handling the load of the iframe
  *
  * @method
  * @param {object} methods - an object containing methods to be passed into the function
  * @param {object} data - an object containing data that needs to be passed into the inner funciton
  * @returns function
  */
export const handleIframeLoaded = ( methods, data )=> {
  return ( e ) => {
    methods.setIframeContentLoaded( true );
  };
};


/**
  * This will open all links of iframe in parent window
  *
  * @method
  * @param {object} methods - an object containing methods to be passed into the function
  * @param {object} data - an object containing data that needs to be passed into the inner funciton
  * @returns function
  */
export const handleIframeContent = ( data, methods )=>{
  const { iframeSrc, iframeRef, iframeContentLoaded } = data || { };
  const { setIframeNoScriptMarkup } = methods || { };

  if( !iframeRef.current && !iframeContentLoaded ){
    return;
  }
  let iframe = iframeRef.current.querySelector( 'iframe' );
  try {
    let contentDocument = iframe.contentDocument || iframe.contentWindow.document;
    if( contentDocument ){
      let pageContent = contentDocument.querySelector( 'body' ).innerHTML;
      setIframeNoScriptMarkup( pageContent );

      // this will open all the links of iframe in parent window
      contentDocument.querySelector( 'body' ).addEventListener( 'click', function( event ){

        let anchor = getParentAnchor( event.target );
        if( anchor !== null ){
          event.preventDefault();
          window.location.href = anchor.href;
        }
      } );
    }
  }
  catch ( e ){
      console.error( `cross-frame-access is not allowed for ${ iframeSrc }` ); //eslint-disable-line
  }
};

/**
  * This will open all links of iframe in parent window
  *
  * @method
  * @param {object} data - an object containing data that needs to be passed into the inner funciton
  * @returns function
  */

export const getParentAnchor = function( element ){
  while ( element !== null ){
    if( element.tagName && element.tagName.toUpperCase() === 'A' ){
      return element;
    }
    // eslint-disable-next-line no-param-reassign
    element = element.parentNode;
  }
  return null;
};

/**
  * This will check if the contenturl is from ulta domain
  *
  * @method
  * @param {object} data - an object containing data that needs to be passed into the inner funciton
  * @param {object} methods - an object containing methods that needs to be passed into the inner funciton
  * @returns function
  */

export const isUltaDomain = function( data, methods ){
  const { contentUrl, isLocal } = data || { };
  const { setIframeSrc } = methods || { };
  if( !contentUrl ){
    return;
  }

  if( isLocal && contentUrl.indexOf( 'https://qa1.ulta.com/innovation' ) !== -1 ){
    const proxyUrl = new URL( 'https://local.ulta.com:8080/iframe-proxy' );
    proxyUrl.searchParams.set( 'source', 'iframe' );
    proxyUrl.searchParams.set( 'url', contentUrl );
    setIframeSrc( proxyUrl.toString() );
    return;
  }

  try {
    if( contentUrl.split( '//' )[1].split( '/' )[0].indexOf( 'ulta.com' ) !== -1 ){
      // setIframeSrc( `${contentUrl}?source=native` )
      setIframeSrc( `${contentUrl}` );
    }
    else {
      setIframeSrc( contentUrl );
    }
  }
  catch ( e ){
     console.error( 'the iframe source is invalid. Please make sure that the url has the full protocol' ); //eslint-disable-line
  }
};


/**
  * Property type definitions
  * @typedef IframedContentProps
  * @type {object}
  * @property {string} example - refactor or delete
  */
export const propTypes =  {
  contentUrl: PropTypes.string,
  fullScreenPagedisplay: PropTypes.bool
};

/**
  * Default values for passed properties
  *
  * @type {object}
  * @property {string} fullScreenPageDisplay - used to determine if we need to use the iframe resizer component for a full screen render of page content
  */
export const defaultProps =  {
  fullScreenPageDisplay: false
};

IframedContent.propTypes = propTypes;
IframedContent.defaultProps = defaultProps;

export default IframedContent;
